/**
 * Versions that belong to a track, including the ones a structure track inherits
 * from the whole-project root it was forked from at the structure split.
 *
 * Since the split stopped copying rows, a forked track's history is
 *   own rows (`file.streamId === fork.id`, uploaded after the split)
 *   ∪ root rows (`file.streamId === fork.forkedFromStreamId`, the shared pre-split history).
 * Own indices always continue from the root's `lastVersionIndex`, so sorting by
 * `versionIndex` gives one continuous newest-first timeline.
 *
 * Legacy forks (split before this change) still carry copies tagged
 * `wholeProjectOrigin`; those are deduped against the root row they mirror.
 */

import type { ProjectFile, ProjectFileStream } from '../types';

type StreamLike = Pick<ProjectFileStream, 'id' | 'forkedFromStreamId' | 'inheritVersionsAfter'>;

const LATE_FORK_GAP_MS = 60_000;

/**
 * When a structure track was added after the original split, root files from
 * before that moment stay off its timeline. The stored field wins. A fork
 * created more than a minute after its siblings is treated the same way, so a
 * structure added before the field existed still starts at its own createdAt.
 */
export function effectiveInheritVersionsAfter(
  stream: Pick<ProjectFileStream, 'id' | 'forkedFromStreamId' | 'createdAt' | 'inheritVersionsAfter'>,
  knownStreams: ReadonlyArray<Pick<ProjectFileStream, 'id' | 'forkedFromStreamId' | 'createdAt'>> = [],
): Date | undefined {
  if (stream.inheritVersionsAfter instanceof Date && !Number.isNaN(stream.inheritVersionsAfter.getTime())) {
    return stream.inheritVersionsAfter;
  }
  if (!stream.forkedFromStreamId || !(stream.createdAt instanceof Date)) return undefined;
  let earliest = Number.POSITIVE_INFINITY;
  for (const other of knownStreams) {
    if (!other.id || other.id === stream.id) continue;
    if (other.forkedFromStreamId !== stream.forkedFromStreamId) continue;
    if (!(other.createdAt instanceof Date)) continue;
    if (other.createdAt.getTime() < earliest) earliest = other.createdAt.getTime();
  }
  if (!Number.isFinite(earliest)) return undefined;
  if (stream.createdAt.getTime() - earliest > LATE_FORK_GAP_MS) return stream.createdAt;
  return undefined;
}

function uploadedOnOrAfter(file: Pick<ProjectFile, 'uploadedAt'>, cutoff: Date): boolean {
  const at = file.uploadedAt instanceof Date ? file.uploadedAt.getTime() : 0;
  return at >= cutoff.getTime();
}

/** True when this document is listed on `streamId` without living there. */
export function isSharedOntoStream(
  file: Pick<ProjectFile, 'streamId' | 'sharedOnStreamIds'>,
  streamId: string | null | undefined,
): boolean {
  if (!streamId || file.streamId === streamId) return false;
  return (file.sharedOnStreamIds ?? []).includes(streamId);
}

type SharedBadgeStream = Pick<
  ProjectFileStream,
  'id' | 'structureNames' | 'title' | 'structureGroupLabel'
>;

/** Rail heading for a track: the renamed group, otherwise its first structure. */
function structureGroupName(stream: SharedBadgeStream | undefined): string {
  const custom = (stream?.structureGroupLabel || '').trim();
  if (custom) return custom;
  const names = (stream?.structureNames ?? []).map(name => name.trim()).filter(Boolean);
  if (names.length > 0) return names[0];
  const title = (stream?.title || '').split('—')[0].trim();
  return title || 'another group';
}

function structureMemberNames(stream: SharedBadgeStream | undefined): string[] {
  return (stream?.structureNames ?? []).map(name => name.trim()).filter(Boolean);
}

/**
 * Badge for one file shown on two or more structure tracks.
 * The label names the other structure group. The hover lists the structures in that group.
 */
export function sharedStructureBadge(
  file: Pick<ProjectFile, 'streamId' | 'sharedOnStreamIds'>,
  streams: readonly SharedBadgeStream[],
  currentStreamId?: string | null,
): { label: string; title: string } | null {
  const ids: string[] = [];
  const seenIds = new Set<string>();
  for (const id of [file.streamId, ...(file.sharedOnStreamIds ?? [])]) {
    if (!id || seenIds.has(id)) continue;
    seenIds.add(id);
    ids.push(id);
  }
  if (ids.length < 2) return null;
  const otherIds = currentStreamId && ids.includes(currentStreamId)
    ? ids.filter(id => id !== currentStreamId)
    : ids.slice(1);
  const others = otherIds.map(id => streams.find(item => item.id === id));
  const groupNames = others.map(structureGroupName);
  const label = groupNames.length === 1
    ? `Shared with ${groupNames[0]}`
    : groupNames.length === 2
      ? `Shared with ${groupNames[0]} and ${groupNames[1]}`
      : `Shared with ${groupNames.slice(0, -1).join(', ')}, and ${groupNames[groupNames.length - 1]}`;
  const title = others.map(stream => {
    const group = structureGroupName(stream);
    const members = structureMemberNames(stream);
    if (members.length === 0) return group;
    if (others.length === 1) return members.join(', ');
    if (members.length === 1 && members[0] === group) return group;
    return `${group}: ${members.join(', ')}`;
  }).join(' · ');
  return { label, title: title || label };
}

/** Root track a fork inherits from, or `null` for ordinary tracks and roots themselves. */
export function rootStreamIdFor(stream: StreamLike | null | undefined): string | null {
  return stream?.forkedFromStreamId || null;
}

/** True when `file` is shown on `stream` but lives on its root (shared pre-split history). */
export function isInheritedVersion(
  file: Pick<ProjectFile, 'streamId'>,
  stream: StreamLike | null | undefined,
): boolean {
  if (!stream?.id || !stream.forkedFromStreamId) return false;
  return file.streamId === stream.forkedFromStreamId;
}

export function compareVersionsNewestFirst(a: ProjectFile, b: ProjectFile): number {
  const at = a.uploadedAt instanceof Date ? a.uploadedAt.getTime() : 0;
  const bt = b.uploadedAt instanceof Date ? b.uploadedAt.getTime() : 0;
  if (at !== bt) return bt - at;
  const ai = a.versionIndex ?? -1;
  const bi = b.versionIndex ?? -1;
  return bi - ai;
}

/**
 * Merge a track's own rows with the rows it inherits from its root, newest first.
 * Pass either the full project file list (filtered here) or the two already-filtered
 * lists via {@link mergeStreamVersions}.
 */
export function versionsForStream(stream: StreamLike | null | undefined, files: ProjectFile[]): ProjectFile[] {
  if (!stream?.id) return [];
  const own = files.filter(f => f.streamId === stream.id || isSharedOntoStream(f, stream.id));
  const rootId = rootStreamIdFor(stream);
  if (!rootId) return own.sort(compareVersionsNewestFirst);
  const seen = new Set(own.map(f => f.id).filter((id): id is string => !!id));
  const cutoff = stream.inheritVersionsAfter instanceof Date ? stream.inheritVersionsAfter : undefined;
  const inherited = files.filter(f => {
    if (f.streamId !== rootId || (f.id && seen.has(f.id))) return false;
    if (cutoff && !uploadedOnOrAfter(f, cutoff)) return false;
    return true;
  });
  return mergeStreamVersions(own, inherited);
}

/**
 * Fold files that are shared onto this track into a stream-scoped subscription.
 * The subscription only hears `streamId` matches; the share list lives on the file.
 */
export function withSharedStreamVersions(
  streamId: string | null | undefined,
  subscribed: ProjectFile[],
  allFiles: ProjectFile[],
): ProjectFile[] {
  if (!streamId) return subscribed;
  const seen = new Set(subscribed.map(f => f.id).filter((id): id is string => !!id));
  const extra = allFiles.filter(f => f.id && !seen.has(f.id) && isSharedOntoStream(f, streamId));
  if (extra.length === 0) return subscribed;
  return [...subscribed, ...extra].sort(compareVersionsNewestFirst);
}

/** Same as {@link versionsForStream} but for lists already scoped to the fork and its root. */
export function mergeStreamVersions(own: ProjectFile[], inherited: ProjectFile[]): ProjectFile[] {
  if (inherited.length === 0) return [...own].sort(compareVersionsNewestFirst);
  // Legacy split copies mirror a root row — show the root row once, not both.
  const mirrored = new Set<string>();
  for (const f of own) if (f.wholeProjectOrigin?.fileId) mirrored.add(f.wholeProjectOrigin.fileId);
  const merged = [...own, ...inherited.filter(f => !f.id || !mirrored.has(f.id))];
  return merged.sort(compareVersionsNewestFirst);
}

/**
 * Head version for a track. The newest upload in this track's history wins
 * (its own rows, or the root it inherits). A stored `headFileId` is kept only
 * when that file is still here and is as new as the newest upload — a pointer
 * left behind after a move, or a higher version number on an older file, does
 * not keep it on top.
 */
export function headVersionForStream(
  stream: (StreamLike & Pick<ProjectFileStream, 'headFileId'>) | null | undefined,
  files: ProjectFile[],
): ProjectFile | undefined {
  if (!stream?.id) return undefined;
  const history = versionsForStream(stream, files);
  const newest = history[0];
  if (!newest || !stream.headFileId) return newest;
  const pointed = history.find(f => f.id === stream.headFileId);
  if (!pointed) return newest;
  return compareVersionsNewestFirst(pointed, newest) === 0 ? pointed : newest;
}
